import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import HomePage from "./pages/Home";
import ThermoimagingPage from "./pages/Thermoimaging";
import SettingsPage from "./pages/Settings";
import ThicknessPage from "./pages/Thickness";
import ColorPage from "./pages/Color";
import ImpressumPage from "./pages/Impressum";
import DatenschutzPage from "./pages/Datenschutz";
import AnalyticsConsentBanner from "./components/AnalyticsConsentBanner";
import ShowAccessGate from "./components/ShowAccessGate";
import { trackAnalyticsEvent, trackAnalyticsPageview } from "./analytics";
import { isShowMode } from "./showMode";
import {
  isShowTourEnabled,
  onShowTourSettingsChange,
  requestShowTourForPath,
  setShowTourEnabled
} from "./tours/showTour";

function Nav() {
  const location = useLocation();
  const [infoMenuOpen, setInfoMenuOpen] = useState(false);
  const [tourEnabled, setTourEnabled] = useState(() => isShowTourEnabled());

  useEffect(() => {
    setInfoMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!infoMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setInfoMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [infoMenuOpen]);

  useEffect(() => onShowTourSettingsChange(setTourEnabled), []);

  const closeInfoMenu = () => setInfoMenuOpen(false);

  return (
    <header className="app-header draggable">
      <div className="app-header-main">
        <div className="app-brand">
          <h1>ZeroCO2 | Process Interface</h1>
        </div>
        <div className="app-header-nav-row">
          <nav className="app-nav no-drag" aria-label="Main navigation">
            <Link
              to="/"
              className="pill app-nav-link app-nav-link-secondary app-nav-link-home-mobile"
              aria-current={location.pathname === "/"}
            >
              Home
            </Link>
            <Link
              to="/thermoimaging"
              className="pill app-nav-link app-nav-link-primary"
              aria-current={location.pathname === "/thermoimaging"}
              onClick={() => trackAnalyticsEvent("nav_thermoimaging_opened")}
            >
              Thermoimaging
            </Link>
            <Link
              to="/thickness"
              className="pill app-nav-link app-nav-link-primary"
              aria-current={location.pathname === "/thickness"}
              onClick={() => trackAnalyticsEvent("nav_thickness_opened")}
            >
              Thickness Plotter
            </Link>
            {!isShowMode && (
              <>
                <Link
                  to="/color"
                  className="pill app-nav-link app-nav-link-secondary"
                  aria-current={location.pathname === "/color"}
                >
                  Color
                </Link>
                <Link
                  to="/settings"
                  className="pill app-nav-link app-nav-link-secondary"
                  aria-current={location.pathname === "/settings"}
                >
                  Settings
                </Link>
              </>
            )}
            {isShowMode && (
              <label
                className="tour-toggle no-drag"
                title="Enable or disable the automatic tutorial"
                data-tour-id="app-tour-toggle"
              >
                <span>Tutorial</span>
                <input
                  type="checkbox"
                  checked={tourEnabled}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    setTourEnabled(enabled);
                    setShowTourEnabled(enabled);
                    if (enabled) {
                      window.setTimeout(() => requestShowTourForPath(location.pathname), 120);
                    }
                  }}
                />
                <span className="tour-toggle-track" aria-hidden="true">
                  <span className="tour-toggle-thumb" />
                </span>
              </label>
            )}
            {isShowMode && (
              <button
                type="button"
                className="pill app-nav-link app-nav-link-secondary app-info-toggle no-drag"
                aria-expanded={infoMenuOpen}
                aria-controls="app-info-nav"
                onClick={() => setInfoMenuOpen((v) => !v)}
              >
                More info {infoMenuOpen ? "▴" : "▾"}
              </button>
            )}
          </nav>
          {isShowMode && (
            <nav
              id="app-info-nav"
              className={`app-info-nav no-drag${infoMenuOpen ? " is-open" : ""}`}
              aria-label="Public information"
            >
              <Link
                to="/impressum"
                className="pill app-info-link"
                aria-current={location.pathname === "/impressum"}
                onClick={closeInfoMenu}
              >
                Legal Notice
              </Link>
              <Link
                to="/datenschutz"
                className="pill app-info-link"
                aria-current={location.pathname === "/datenschutz"}
                onClick={closeInfoMenu}
              >
                Privacy Policy
              </Link>
              <a
                className="pill app-info-link"
                href="https://www.marvinbay.de"
                target="_blank"
                rel="noreferrer"
                onClick={closeInfoMenu}
              >
                Marvin Bay
              </a>
              <a
                className="pill app-info-link"
                href="https://www.ipgr.com"
                target="_blank"
                rel="noreferrer"
                onClick={closeInfoMenu}
              >
                IPGR
              </a>
              <a
                className="pill app-info-link"
                href="https://www.ghi.rwth-aachen.de"
                target="_blank"
                rel="noreferrer"
                onClick={closeInfoMenu}
              >
                GHI - RWTH
              </a>
            </nav>
          )}
        </div>
      </div>
    </header>
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    trackAnalyticsPageview();
  }, [location.pathname, location.search, location.hash]);

  return null;
}

export default function App() {
  return (
    <ShowAccessGate>
      <div className="app-shell">
        <AnalyticsRouteTracker />
        <Nav />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/thermoimaging" element={<ThermoimagingPage />} />
          <Route path="/thickness" element={<ThicknessPage />} />
          <Route path="/color" element={isShowMode ? <Navigate to="/" replace /> : <ColorPage />} />
          <Route
            path="/settings"
            element={isShowMode ? <Navigate to="/" replace /> : <SettingsPage />}
          />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <AnalyticsConsentBanner />
    </ShowAccessGate>
  );
}
